import { useMutation } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { useAuth } from '@/app/providers/AuthProvider';
import { ApiError, errorMessage } from '@/lib/api/errors';
import { Button, Input } from '@/ui';
import { changePassword } from './api';
import { AuthShell } from './AuthShell';
import { NewPasswordFields, validateNewPassword } from './PasswordFields';
import type { PasswordPair } from './PasswordFields';
import styles from './AuthForm.module.css';

/**
 * The forced first-time password change.
 *
 * This gate is entirely ours: the backend reports `must_change_password` but a token
 * carrying it is fully valid on every other route, so nothing server-side prevents the
 * user from working. The router keeps them here until it clears.
 *
 * Note the trap: `current_password` is verified even for this forced change, so the
 * user must supply the temporary password they just signed in with. Sending null
 * returns 400.
 */
export function ForcePasswordChangePage() {
  const { clearPasswordGate, signOut, user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [pair, setPair] = useState<PasswordPair>({ next: '', confirm: '' });

  const mutation = useMutation({
    mutationFn: () => changePassword({ currentPassword, newPassword: pair.next }),
    onSuccess: clearPasswordGate,
  });

  const validation = validateNewPassword(pair);
  const canSubmit = validation.ok && currentPassword !== '' && pair.next !== currentPassword;

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!canSubmit) return;
    mutation.mutate();
  };

  // 400 here means the current password was wrong — the most likely mistake on this
  // screen, and worth naming precisely instead of showing a generic failure.
  const wrongCurrent = mutation.error instanceof ApiError && mutation.error.status === 400;

  return (
    <AuthShell
      title="Set a new password"
      subtitle={
        user?.fullName !== null && user?.fullName !== undefined
          ? `Welcome, ${user.fullName}. Choose a password before continuing.`
          : 'Choose a password before continuing.'
      }
      footer={
        <button type="button" className={styles.link} onClick={() => signOut()}>
          Sign out instead
        </button>
      }
    >
      <form className={styles.form} onSubmit={onSubmit} noValidate>
        {mutation.isError && (
          <p className={styles.formError} role="alert">
            {wrongCurrent
              ? 'That current password is not correct.'
              : errorMessage(mutation.error)}
          </p>
        )}

        <Input
          label="Current password"
          icon={Lock}
          type="password"
          value={currentPassword}
          onChange={(event) => setCurrentPassword(event.target.value)}
          autoComplete="current-password"
          helperText="The temporary password you just signed in with."
          required
          disabled={mutation.isPending}
        />

        <NewPasswordFields value={pair} onChange={setPair} disabled={mutation.isPending} />

        {pair.next !== '' && pair.next === currentPassword && (
          <p className={styles.formError} role="alert">
            The new password must be different from the current one.
          </p>
        )}

        <Button
          type="submit"
          fullWidth
          size="lg"
          loading={mutation.isPending}
          disabled={!canSubmit}
        >
          Update password and continue
        </Button>
      </form>
    </AuthShell>
  );
}
